/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import "./dashboard.scss";
import FilterDashboard from "./components/filter";
import dayjs from "dayjs";
import {
  IChartByUser,
  IHeadlinesSummary,
  IPaymentDueSummary,
  IPaymentSummary,
  ITinyBarChart,
} from "../../interfaces/dashboard";
import TinyBarChart from "../../components/chart/tiny-bar-chart";
import YtdTopSupplierChart from "./components/ytd-top-supplier-chart";
import { DashboardServices } from "../../services/dashboard";
import { formatDateFieldsFlat } from "../../utils/helper";
import Loading from "../../components/loading";
import { Col, Collapse, Flex, Row, Select, Switch } from "antd";
import StackedBarChart from "../../components/chart/stacked-bar-chart";
import { rowsFieldSirInterventionBySuppliers, rowsFieldTeamPerformanceByApOwner } from "../../constants/dashboard";
import FmPieChart from "../../components/chart/fm-pie-chart";
import HeadlinesSummary from "./components/headlines-summary";
import PaymentsDueSummary from "./components/payments-due-summary";
import PaymentsSummary from "./components/payments-summary";
import FmPieChartGrid from "../../components/chart/fm-pie-chart-grid";

const { Panel } = Collapse;

const VIEWS_BY = [
  {
    value: "volume",
    label: "Invoices Volume",
  },
  {
    value: "value",
    label: "Invoices Value",
  },
];

const initialValues = {
  supplierIds: null,
  startDate: dayjs().subtract(30, "day"),
  endDate: dayjs(),
};

const initialYtdTopSupplierChartValues = {
  statuses: null,
  viewBy: "volume",
};

async function fetchYtdTopSupplierChart(params: any): Promise<ITinyBarChart[]> {
  const res = await DashboardServices.getTopSuppliersReport(params);
  const { data = [] } = res;
  return data.map((d) => ({ label: d.supplierName, value: d.totalValue }));
}

async function fetchTopSupplierError(params: any): Promise<ITinyBarChart[]> {
  const res = await DashboardServices.getTopSuppliersError(params);
  const { data = [] } = res;
  return data.map((d) => ({ label: d.supplierName, value: d.totalValue }));
}

async function fetchTeamAction(params: any): Promise<ITinyBarChart[]> {
  const res = await DashboardServices.getTeamActionReport(params);
  const { data = [] } = res;
  return data.map((d) => ({ label: d.team, value: d.count }));
}

const DashboardPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [dataFilter, setDataFilter] = useState(initialValues);
  const [dataFilterYtdTopSupplierChart, setDataFilterYtdTopSupplierChart] = useState(initialYtdTopSupplierChartValues);
  const [dataChartYtdTopSupplier, setDataChartYtdTopSupplier] = useState<ITinyBarChart[]>([]);
  const [dataChartTopSupplierError, setDataChartTopSupplierError] = useState<ITinyBarChart[]>([]);
  const [dataChartSirInterventionBySuppliers, setDataChartSirInterventionBySuppliers] = useState<any[]>([]);
  const [dataChartTeamPerformanceByApOwner, setDataChartTeamPerformanceByApOwner] = useState<any[]>([]);
  const [dataChartSirReviewedVolumeBySupplierReport, setDataChartSirReviewedVolumeBySupplierReport] = useState<
    IChartByUser[]
  >([]);
  const [dataChartSirReviewedValueBySupplierReport, setDataChartSirReviewedValueBySupplierReport] = useState<
    IChartByUser[]
  >([]);
  const [dataChartTeamAction, setDataChartTeamAction] = useState<any[]>([]);
  const [dataSummaryHeadlines, setDataSummaryHeadlines] = useState<IHeadlinesSummary>();
  const [dataSummaryPaymentsDue, setDataSummaryPaymentsDue] = useState<IPaymentDueSummary[]>();

  const [dataSummaryPayments, setDataSummaryPayments] = useState<IPaymentSummary>();

  const [dataChartInvoiceBySupplier, setDataChartInvoiceBySupplier] = useState<ITinyBarChart[]>([]);
  const [dataSirMatchingAccuracy, setDataSirMatchingAccuracy] = useState<ITinyBarChart[]>([]);
  const [dataChartAiPerformance, setDataChartAiPerformance] = useState<ITinyBarChart[]>([]);

  const [filterInv, setFilterInv] = useState(VIEWS_BY[0].value);

  const [localCurrentyType, setLocalCurrentyType] = useState("gbp");

  const getDataFilterYtdTopSupplier = () => {
    return {
      ...dataFilter,
      ...dataFilterYtdTopSupplierChart,
      top: 10,
    };
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const paramsFilter = formatDateFieldsFlat(dataFilter);
      const requests = [
        fetchYtdTopSupplierChart(formatDateFieldsFlat(getDataFilterYtdTopSupplier())),
        fetchTopSupplierError(paramsFilter),
        DashboardServices.getSirInterventionBySuppliers(paramsFilter),
        DashboardServices.getTeamPerformanceByApOwner(paramsFilter),
        DashboardServices.getSirReviewedBySupplierReport({ ...paramsFilter, viewBy: "volume" }),
        DashboardServices.getSirReviewedBySupplierReport({ ...paramsFilter, viewBy: "value" }),
        fetchTeamAction(paramsFilter),
        DashboardServices.getHeadlinesSummary(paramsFilter),
        DashboardServices.getPaymentsSummary({}),
        DashboardServices.getPaymentsDueSummary({ ...paramsFilter, currencyType: localCurrentyType }),
        DashboardServices.getInvoiceBySupplierReport({ ...paramsFilter, viewBy: filterInv }),
        DashboardServices.getSirMatchingAccuracyBySupplierReport(paramsFilter),
        DashboardServices.getAiPerformanceBySupplierReport(paramsFilter),
      ];

      const results: any = await Promise.allSettled(requests);
      const get = (i: number) => (results[i].status === "fulfilled" ? results[i]?.value : null);

      setDataChartYtdTopSupplier(get(0));
      setDataChartTopSupplierError(get(1));
      setDataChartSirInterventionBySuppliers(get(2)?.data || []);
      setDataChartTeamPerformanceByApOwner(get(3)?.data || []);
      setDataChartSirReviewedVolumeBySupplierReport(get(4)?.data || []);
      setDataChartSirReviewedValueBySupplierReport(get(5)?.data || []);
      setDataChartTeamAction(get(6));
      setDataSummaryHeadlines(get(7)?.data?.[0] || {});
      setDataSummaryPayments(get(8)?.data?.[0] || {});
      setDataSummaryPaymentsDue(get(9)?.data || []);
      setDataChartInvoiceBySupplier(get(10)?.data || {});
      setDataSirMatchingAccuracy(get(11)?.data || {});
      setDataChartAiPerformance(get(12)?.data || {});
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dataFilter]);

  const handleChangeYtdTopSupplier = async (values: any) => {
    try {
      const params = { ...getDataFilterYtdTopSupplier(), ...values };
      const resYtdTopSupplier = await fetchYtdTopSupplierChart(formatDateFieldsFlat(params));
      setDataFilterYtdTopSupplierChart({ ...dataFilterYtdTopSupplierChart, ...values });
      setDataChartYtdTopSupplier(resYtdTopSupplier);
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  };

  const handleChangeViewInv = async (value: string) => {
    setFilterInv(value);
    try {
      const paramsFilter = formatDateFieldsFlat(dataFilter);
      const res = await DashboardServices.getInvoiceBySupplierReport({ ...paramsFilter, viewBy: value });
      setDataChartInvoiceBySupplier(res?.data || {});
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleChangeCurrencyType = async (value: boolean) => {
    setLocalCurrentyType(value ? "local" : "gbp");
    try {
      const paramsFilter = formatDateFieldsFlat(dataFilter);
      const res = await DashboardServices.getPaymentsDueSummary({
        ...paramsFilter,
        currencyType: value ? "local" : "gbp",
      });
      setDataSummaryPaymentsDue(res?.data || {});
    } catch (error: any) {
      console.log(error);
    }
  };

  const renderContentBox = (title: string, children: React.ReactNode) => (
    <div className="content-box-fm">
      <h3 className="title-box">{title}</h3>
      <Row className="scroll-chart" gutter={16}>
        {children}
      </Row>
    </div>
  );

  const renderContentCollapseBox = (title: string, children: React.ReactNode, extra?: React.ReactNode) => (
    <Collapse bordered={false} defaultActiveKey={["1"]} className="content-box-fm" expandIconPosition="right">
      <Panel header={<h3 className="title-box">{title}</h3>} key="0" extra>
        {extra ?? <Row> {extra} </Row>}
        <Row className="scroll-chart" gutter={16}>
          {children}
        </Row>
      </Panel>
    </Collapse>
  );

  const currencySwitch = (
    <Switch
      checked={localCurrentyType === "local"}
      onChange={handleChangeCurrencyType}
      checkedChildren="Local Currency"
      unCheckedChildren="GBP Currency"
    />
  );

  const renderContentChart = (title: string, children: React.ReactNode) => (
    <Col span={8}>
      <div className="chart-fm">
        {title && <h4 className="title-chart">{title}</h4>}
        {children}
      </div>
    </Col>
  );

  const renderContentSupplierDetails = () => (
    <>
      {renderContentChart(
        "YTD vs Top Suppliers",
        <YtdTopSupplierChart
          initialValues={dataFilterYtdTopSupplierChart}
          onChangeFilter={handleChangeYtdTopSupplier}
          dataChart={dataChartYtdTopSupplier}
          loading={isLoading}
        />
      )}
      {renderContentChart(
        "SIR Intervention by Supplier",
        <StackedBarChart
          data={dataChartSirInterventionBySuppliers}
          rowsField={rowsFieldSirInterventionBySuppliers}
          fieldName="supplierName"
          loading={isLoading}
          height={450}
        />
      )}
      {renderContentChart(
        "Supplier Errors - Top Offenders",
        <TinyBarChart data={dataChartTopSupplierError} color="#FF4D4F" loading={isLoading} height={450} />
      )}
    </>
  );

  const renderContentUserWorkflow = () => (
    <>
      {renderContentChart(
        "SIR to be Reviewed by User (Volume)",
        <FmPieChart data={dataChartSirReviewedVolumeBySupplierReport} loading={isLoading} />
      )}
      {renderContentChart(
        "SIR to be Reviewed by User (Value)",
        <FmPieChart data={dataChartSirReviewedValueBySupplierReport} loading={isLoading} />
      )}
      {renderContentChart(
        "Team Performance By AP Owner",
        <StackedBarChart
          data={dataChartTeamPerformanceByApOwner}
          rowsField={rowsFieldTeamPerformanceByApOwner}
          fieldName="apOwner"
          loading={isLoading}
          height={dataChartTeamPerformanceByApOwner.length * 31 + 50}
        />
      )}
    </>
  );

  const renderContentSirsTeamReview = () => (
    <>
      {renderContentChart("Outstanding Team Actions", <TinyBarChart data={dataChartTeamAction} loading={isLoading} />)}
    </>
  );

  const renderContentHeadlinesSummary = () =>
    dataSummaryHeadlines && (
      <HeadlinesSummary
        data={dataSummaryHeadlines}
        loading={isLoading}
        dataFilter={{
          supplierIds: dataFilter?.supplierIds,
        }}
      />
    );

  const renderContentSupplierPaymentsDue = () =>
    dataSummaryPaymentsDue && <PaymentsDueSummary data={dataSummaryPaymentsDue} loading={isLoading} />;

  const renderContentSupplierPayments = () =>
    dataSummaryPayments && <PaymentsSummary data={dataSummaryPayments} loading={isLoading} />;

  const renderContentInvoiceData = () => (
    <>
      {renderContentChart("", renderChartInvoice())}
      {renderContentChart("SIR Matching Accuracy", <TinyBarChart data={dataSirMatchingAccuracy} loading={isLoading} />)}
      {renderContentChart("AI Performance", <FmPieChartGrid data={dataChartAiPerformance} loading={isLoading} />)}
    </>
  );

  const renderChartInvoice = () => (
    <>
      <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
        <span>View by </span>{" "}
        <Select
          value={filterInv}
          options={VIEWS_BY}
          placeholder="View by"
          onChange={handleChangeViewInv}
          style={{ width: 200 }}
        />
      </Flex>
      <TinyBarChart data={dataChartInvoiceBySupplier} loading={isLoading} />
    </>
  );

  return (
    <>
      {isLoading && <Loading />}
      <div className="dashboard-page">
        <h2 className="title-page">Dashboard</h2>
        {renderContentCollapseBox("Expected Supplier Payment", renderContentSupplierPayments())}
        <FilterDashboard onChangeFilter={(value: any) => setDataFilter(value)} initialValues={dataFilter} />
        {renderContentHeadlinesSummary()}
        {renderContentCollapseBox("Supplier Payment Due", renderContentSupplierPaymentsDue(), currencySwitch)}
        {renderContentBox("Supplier Details", renderContentSupplierDetails())}
        {renderContentBox("User Workflow", renderContentUserWorkflow())}
        {renderContentBox("Invoices Data", renderContentInvoiceData())}
        {renderContentBox("SIR Team Reviews", renderContentSirsTeamReview())}
      </div>
    </>
  );
};

export default DashboardPage;
